import React, { useState } from "react"
import PropTypes from "prop-types"
import i18n from "i18n"
import { Box, IconButton, Tooltip } from "@mui/material"
import BarChartIcon from "@mui/icons-material/BarChart"
import PieChartIcon from "@mui/icons-material/PieChart"

import DonutChartCard from "./DonutChartCard"
import BarChartCard from "./BarChartCard"
import ExpensesCard from "./ExpensesCard"
import {
  overviewCardsGridSx4,
  overviewTwoPanelItemGridColumn,
} from "./constants/overviewGrid"

const DONUT = "donut"
const BAR = "bar"

const ExpensesSection = ({ data, isLoading }) => {
  const [chartType, setChartType] = useState(BAR)

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ width: "100%", mb: 2 }}>
        <ExpensesCard
          data={isLoading ? null : data?.summary}
          currency={data?.currency}
        />
      </Box>
      <Box sx={{ ...overviewCardsGridSx4, alignItems: "stretch" }}>
        <Box
          sx={{
            gridColumn: overviewTwoPanelItemGridColumn,
            minWidth: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <DonutChartCard
            title={i18n.t("overview.nature")}
            infoText={i18n.t("overview.tooltip.nature")}
            dataSource={data?.natureChart ?? []}
            isLoading={isLoading}
            currency={data?.currency}
          />
        </Box>

        <Box
          sx={{
            gridColumn: overviewTwoPanelItemGridColumn,
            position: "relative",
            minWidth: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {!isLoading && (
            <Tooltip
              title={
                chartType === DONUT
                  ? i18n.t("overview.changeToBars")
                  : i18n.t("overview.changeToDonut")
              }
              placement="top"
            >
              <IconButton
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  setChartType(chartType === DONUT ? BAR : DONUT)
                }}
                aria-label={
                  chartType === DONUT
                    ? i18n.t("overview.changeToBars")
                    : i18n.t("overview.changeToDonut")
                }
                sx={{
                  position: "absolute",
                  bottom: 14,
                  left: 14,
                  zIndex: 1,
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(4px)",
                  border: "1px solid rgba(0, 0, 0, 0.12)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 1)",
                  },
                }}
                size="small"
              >
                {chartType === DONUT ? <BarChartIcon /> : <PieChartIcon />}
              </IconButton>
            </Tooltip>
          )}

          {chartType === DONUT ? (
            <DonutChartCard
              key="donut-breakdown"
              title={i18n.t("overview.expenseDirectType")}
              infoText={i18n.t("overview.tooltip.expenseDirectType")}
              dataSource={data?.breakdownChart ?? []}
              isLoading={isLoading}
              currency={data?.currency}
            />
          ) : (
            <BarChartCard
              key="bar-breakdown"
              title={i18n.t("overview.expenseDirectType")}
              infoText={i18n.t("overview.tooltip.expenseDirectType")}
              dataSource={data?.breakdownChart ?? []}
              isLoading={isLoading}
              currency={data?.currency}
              reserveFooterSlot
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}

ExpensesSection.propTypes = {
  data: PropTypes.shape({
    currency: PropTypes.string,
    summary: PropTypes.object,
    natureChart: PropTypes.arrayOf(PropTypes.object),
    breakdownChart: PropTypes.arrayOf(PropTypes.object),
  }),
  isLoading: PropTypes.bool.isRequired,
}

export default ExpensesSection
